/**
 * ProductCard Component
 *
 * Expandable card displaying a Product (industry vertical) with its nested Schema Groups.
 * Shows product info, status, type badge (Platform/Organization), and group count.
 * Expands to reveal groups.
 *
 * Platform products: TerraMatrix-controlled, edit/delete requires admin
 * Organization products: Org-controlled, edit/delete allowed (except default product)
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Product,
  SchemaGroup,
} from '@/services/api/groupApi';
import { SchemaGroupCard } from './SchemaGroupCard';
import {
  ChevronRight,
  Pencil,
  Trash2,
  Beer,
  Wheat,
  Leaf,
  Droplet,
  Zap,
  Factory,
  Package,
  Globe,
  Building2,
  Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Product icons using Lucide components for cross-platform consistency
const PRODUCT_ICONS: Record<string, React.ReactNode> = {
  brewing: <Beer className="h-6 w-6 text-amber-600" />,
  agriculture: <Wheat className="h-6 w-6 text-green-600" />,
  environmental: <Leaf className="h-6 w-6 text-emerald-600" />,
  water: <Droplet className="h-6 w-6 text-blue-600" />,
  energy: <Zap className="h-6 w-6 text-yellow-600" />,
  manufacturing: <Factory className="h-6 w-6 text-gray-600" />,
  default: <Package className="h-6 w-6 text-gray-600" />,
};

interface ProductCardProps {
  product: Product;
  groups: SchemaGroup[];
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleStatus: () => void;
  onAddGroup: () => void;
  onEditGroup: (group: SchemaGroup) => void;
  onDeleteGroup: (group: SchemaGroup) => void;
  onManageSchemas: (group: SchemaGroup) => void;
  onAssignOrganizations: (group: SchemaGroup) => void;
  isAdmin?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  groups,
  isExpanded,
  onToggleExpanded,
  onEdit,
  onDelete,
  onToggleStatus,
  onAddGroup,
  onEditGroup,
  onDeleteGroup,
  onManageSchemas,
  onAssignOrganizations,
  isAdmin = false,
}) => {
  // Get icon - use product's icon, or match by slug, or default
  const icon = PRODUCT_ICONS[product.slug] || PRODUCT_ICONS.default;
  const isActive = product.status === 'active';
  const isPlatform = product.type === 'platform';
  const isDefault = product.isDefault ?? false;

  // Determine if edit/delete should be allowed
  // Platform products: only admins can edit/delete
  // Default org products: cannot be deleted
  const canEdit = isPlatform ? isAdmin : true;
  const canDelete = isPlatform ? isAdmin : !isDefault;
  const canToggleStatus = isPlatform ? isAdmin : true;
  const canManageGroups = isPlatform ? isAdmin : true;

  return (
    <div className={`border border-border rounded-lg overflow-hidden ${!isActive ? 'opacity-60' : ''}`}>
      {/* Product Header */}
      <div
        className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-4">
          {/* Expand/Collapse Icon */}
          <ChevronRight
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              isExpanded && "rotate-90"
            )}
          />

          {/* Product Icon */}
          <span className="flex-shrink-0">{icon}</span>

          {/* Product Info */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold">{product.name}</h3>
              {/* Type badge */}
              <span
                className={cn(
                  "px-2 py-0.5 text-xs rounded flex items-center gap-1",
                  isPlatform
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                )}
              >
                {isPlatform ? (
                  <>
                    <Globe className="h-3 w-3" />
                    Platform
                  </>
                ) : (
                  <>
                    <Building2 className="h-3 w-3" />
                    Organization
                  </>
                )}
              </span>
              {/* Default badge */}
              {isDefault && (
                <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Default
                </span>
              )}
              {/* Status badge */}
              <span
                className={`px-2 py-0.5 text-xs rounded ${
                  isActive
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                }`}
              >
                {product.status}
              </span>
              {/* Custom domain badge */}
              {product.customDomain && (
                <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                  {product.customDomain}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {product.description || 'No description'} • {groups.length} group{groups.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {canToggleStatus && (
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleStatus}
              title={isActive ? 'Deactivate product' : 'Activate product'}
            >
              {isActive ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label="Edit product"
              title="Edit product"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label="Delete product"
              title={isDefault ? "Cannot delete default product" : "Delete product"}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Expanded Content - Groups */}
      {isExpanded && (
        <div className="border-t border-border animate-in slide-in-from-top-1 duration-200">
          {/* Groups Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-background">
            <span className="text-sm font-medium text-muted-foreground">
              Groups ({groups.length})
            </span>
            {canManageGroups && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAddGroup}
              >
                + Add Group
              </Button>
            )}
          </div>

          {/* Groups List */}
          {groups.length === 0 ? (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <p>No groups in this product yet.</p>
              {canManageGroups && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={onAddGroup}
                >
                  Create your first group
                </Button>
              )}
            </div>
          ) : (
            <div className="p-4 pt-0 space-y-3">
              {groups.map((group) => (
                <SchemaGroupCard
                  key={group.id}
                  group={group}
                  schemaCount={group.schemaCount ?? 0}
                  onEdit={() => onEditGroup(group)}
                  onDelete={() => onDeleteGroup(group)}
                  onManageSchemas={() => onManageSchemas(group)}
                  onAssignOrganizations={() => onAssignOrganizations(group)}
                  canManage={canManageGroups}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCard;
